import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEnrollments, useMe, useAllActiveSessions, useDeleteSession, useShareStats, useUnenrollSection, type StudySession } from '../lib/api';

export function ModulesPage() {
  const { data: me } = useMe();
  const { data: enrollments } = useEnrollments();
  const { data: sessionsData } = useAllActiveSessions();
  const { data: shareStatsData } = useShareStats();
  const deleteSession = useDeleteSession();
  const unenroll = useUnenrollSection();
  const navigate = useNavigate();
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [classSearch, setClassSearch] = useState('');
  const [confirmUnenrollId, setConfirmUnenrollId] = useState<string | null>(null);

  const hasClasses = (enrollments?.enrollments?.length ?? 0) > 0;
  const [dismissedWelcome, setDismissedWelcome] = useState(false);
  const showWelcome = enrollments && !hasClasses && !dismissedWelcome;

  const stats = me?.stats;
  const dailyGoal = 30;
  const todayDone = stats?.questionsToday ?? 0;
  const dailyProgress = Math.min(100, Math.round((todayDone / dailyGoal) * 100));
  const dailyDone = todayDone >= dailyGoal;

  const sessions = sessionsData?.sessions ?? [];
  const totalShares = stats?.totalShares ?? shareStatsData?.total_shares ?? 0;
  const sharedToday = stats?.sharedToday ?? shareStatsData?.shared_today ?? 0;
  const totalAnswers = shareStatsData?.total_answers ?? 0;
  const correctPct = shareStatsData?.correct_percentage ?? 0;

  const [expandedPanel, setExpandedPanel] = useState<'shares' | 'daily' | null>(null);
  const togglePanel = (panel: 'shares' | 'daily') => setExpandedPanel(prev => prev === panel ? null : panel);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const handleConfirmDelete = () => {
    if (confirmDeleteId) {
      deleteSession.mutate(confirmDeleteId);
      setConfirmDeleteId(null);
    }
  };

  // Filter classes by search
  const allClasses = enrollments?.enrollments ?? [];
  const filteredClasses = useMemo(() => {
    if (!classSearch.trim()) return allClasses;
    const q = classSearch.toLowerCase();
    return allClasses.filter((e: any) =>
      e.class_code?.toLowerCase().includes(q) ||
      e.school_name?.toLowerCase().includes(q) ||
      e.professor_name?.toLowerCase().includes(q)
    );
  }, [allClasses, classSearch]);

  const getSessionContext = (s: StudySession & { contextName?: string }) => {
    if (s.contextName) return s.contextName;
    if (s.classSectionId) {
      const enrollment = enrollments?.enrollments?.find((e: any) => e.id === s.classSectionId);
      return enrollment ? enrollment.class_code : 'Class';
    }
    if (s.examSectionId) return 'Exam';
    return 'Study';
  };

  const resumeSession = (s: StudySession) => {
    const mode = (s.filters as any)?.mode;
    if (s.classSectionId && mode === 'flashcard') navigate(`/flashcards/${s.classSectionId}?session=${s.id}`);
    else if (s.classSectionId && mode === 'quick') navigate(`/quickmem/${s.classSectionId}?session=${s.id}`);
    else if (s.classSectionId) navigate(`/study/class/${s.classSectionId}?session=${s.id}`);
    else if (s.examSectionId) navigate(`/study/exam/${s.examSectionId}?session=${s.id}`);
  };

  const firstName = me?.user?.firstName;

  return (
    <div className="space-y-5">
      {/* ── Welcome ────────────────────────────────────────────────── */}
      {firstName && (
        <p className="text-sm text-slate-500">
          Welcome back, <span className="font-semibold text-squishy-navy">{firstName}</span>
        </p>
      )}

      {/* ── Compact Stats Row ─────────────────────────────────────── */}
      <div className="flex gap-2">
        {/* Questions Shared pill */}
        <button
          onClick={() => togglePanel('shares')}
          className="flex-1 bg-white border rounded-xl px-3 py-2.5 flex items-center gap-2.5 hover:shadow-sm transition-all active:scale-[0.98]"
        >
          <span className="text-xs font-bold text-white bg-gradient-to-br from-indigo-400 to-purple-500 rounded-lg px-2 py-1 shrink-0">
            {totalShares}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-squishy-navy">Shared</div>
            <div className="text-[10px] text-slate-400">{sharedToday} today</div>
          </div>
          <svg className={`w-3.5 h-3.5 text-slate-300 shrink-0 transition-transform duration-200 ${expandedPanel === 'shares' ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </button>

        {/* Daily goal pill */}
        <button
          onClick={() => togglePanel('daily')}
          className="bg-white border rounded-xl px-3 py-2.5 flex items-center gap-2 hover:shadow-sm transition-all active:scale-[0.98] shrink-0"
        >
          <div className="relative w-8 h-8">
            <svg className="w-8 h-8 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15" fill="none" stroke="#f1f5f9" strokeWidth="3" />
              <circle cx="18" cy="18" r="15" fill="none" stroke={dailyDone ? '#2dd4bf' : '#0d9488'} strokeWidth="3"
                strokeDasharray={`${dailyProgress * 0.9425} 94.25`} strokeLinecap="round" className="transition-all duration-500" />
            </svg>
            {dailyDone && (
              <span className="absolute inset-0 flex items-center justify-center text-[10px]">&#x2713;</span>
            )}
            {!dailyDone && (
              <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-squishy-primary">{todayDone}</span>
            )}
          </div>
          <div className="text-left">
            <div className="text-[10px] font-semibold text-slate-600">Today</div>
            <div className="text-[10px] text-slate-400">{todayDone}/{dailyGoal}</div>
          </div>
          <svg className={`w-3.5 h-3.5 text-slate-300 shrink-0 transition-transform duration-200 ${expandedPanel === 'daily' ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* ── Expandable panels ──────────────────────────────────────── */}
      <div>
      <ExpandPanel open={expandedPanel === 'shares'}>
        <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl p-4 text-white">
          <div className="flex items-baseline justify-between">
            <span className="text-lg font-bold">Questions Shared</span>
            <span className="text-sm opacity-80">{totalShares} total</span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="bg-white/15 rounded-lg py-2">
              <div className="text-lg font-bold">{sharedToday}</div>
              <div className="text-[10px] opacity-70">Today</div>
            </div>
            <div className="bg-white/15 rounded-lg py-2">
              <div className="text-lg font-bold">{totalAnswers}</div>
              <div className="text-[10px] opacity-70">Answered</div>
            </div>
            <div className="bg-white/15 rounded-lg py-2">
              <div className="text-lg font-bold">{correctPct}%</div>
              <div className="text-[10px] opacity-70">Correct</div>
            </div>
          </div>
        </div>
      </ExpandPanel>
      <ExpandPanel open={expandedPanel === 'daily'}>
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold text-squishy-navy">Today's Goal</span>
            <span className="text-xs text-slate-500">{todayDone} / {dailyGoal}</span>
          </div>
          <div className="mt-2 h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${dailyDone ? 'bg-emerald-400' : 'bg-squishy-teal'}`}
              style={{ width: `${dailyProgress}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {todayDone === 0 ? `Answer ${dailyGoal} questions to keep your streak alive.`
              : dailyDone ? `Goal crushed! You answered ${todayDone} questions today.`
              : `${dailyGoal - todayDone} to go!`}
          </p>
        </div>
      </ExpandPanel>
      </div>

      {/* ── Resume where you left off ────────────────────────────── */}
      {sessions.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-squishy-navy mb-2">Resume where you left off</h2>
          <div className="overflow-x-auto no-scrollbar -mx-4 px-4" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="inline-flex gap-3">
              {sessions.slice(0, 6).map((s) => (
                <SessionCard
                  key={s.id}
                  session={s}
                  contextName={getSessionContext(s)}
                  onResume={() => resumeSession(s)}
                  onDiscard={() => setConfirmDeleteId(s.id)}
                />
              ))}
            </div>
          </div>
          {sessions.length > 2 && (
            <button
              onClick={() => setShowAllSessions(true)}
              className="mt-2 text-xs font-semibold text-squishy-tealDark hover:underline"
            >
              More &gt;&gt;
            </button>
          )}
        </section>
      )}

      {/* Fallback CTA if no sessions */}
      {sessions.length === 0 && hasClasses && (
        <Link
          to={`/class/${enrollments!.enrollments[0].id}`}
          className="block rounded-xl p-4 text-white shadow-soft hover:opacity-95 transition"
          style={{ background: 'linear-gradient(135deg, #1e1b4b, #0d9488)' }}
        >
          <div className="text-xs uppercase tracking-wide opacity-80">Start studying</div>
          <div className="text-lg font-bold mt-0.5">
            {enrollments!.enrollments[0].class_code}
          </div>
        </Link>
      )}

      {/* ── My Classes ─ Vertical List with Search ────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-squishy-navy">My Classes</h2>
          <Link
            to="/onboarding"
            className="inline-flex items-center gap-1.5 bg-gradient-to-r from-squishy-primary to-teal-600 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-sm hover:opacity-90 transition active:scale-[0.97]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add Class
          </Link>
        </div>

        {/* Search bar (show if 4+ classes) */}
        {allClasses.length >= 4 && (
          <div className="relative mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              value={classSearch}
              onChange={(e) => setClassSearch(e.target.value)}
              placeholder="Search your classes..."
              className="w-full bg-white border rounded-xl pl-9 pr-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-squishy-primary/30 focus:border-squishy-primary transition"
            />
          </div>
        )}

        {filteredClasses.length > 0 ? (
          <div className="space-y-2">
            {filteredClasses.map((e: any) => (
              <div key={e.id} className="flex items-center gap-3 bg-white border rounded-xl p-3.5 hover:shadow-md transition group">
                <Link
                  to={`/class/${e.id}`}
                  className="flex items-center gap-3 flex-1 min-w-0 active:scale-[0.99] transition-transform"
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-squishy-dark to-purple-700 flex items-center justify-center text-white text-[11px] font-bold shrink-0 tracking-tight">
                    {e.class_code?.substring(0, 4)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-squishy-navy">{e.class_code}</div>
                    <div className="text-xs text-slate-400 truncate">
                      {e.school_name}{e.professor_name ? ` · Prof. ${e.professor_name}` : ''}{e.term ? ` · ${e.term}` : ''}
                    </div>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-slate-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
                {confirmUnenrollId === e.id ? (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => { unenroll.mutate(e.id); setConfirmUnenrollId(null); }}
                      className="text-[10px] font-semibold text-red-600 bg-red-50 px-2 py-1 rounded-lg hover:bg-red-100 transition"
                    >
                      Remove
                    </button>
                    <button
                      onClick={() => setConfirmUnenrollId(null)}
                      className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-1 rounded-lg hover:bg-slate-200 transition"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmUnenrollId(e.id)}
                    className="shrink-0 text-slate-300 hover:text-red-400 transition p-1 opacity-0 group-hover:opacity-100"
                    title="Remove class"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : allClasses.length > 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">No classes match &ldquo;{classSearch}&rdquo;</p>
        ) : (
          <Link
            to="/onboarding"
            className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center text-sm text-slate-500 hover:border-squishy-primary hover:text-squishy-primary transition"
          >
            + Add your first class
          </Link>
        )}
      </section>

      {/* ── All Sessions Overlay ──────────────────────────────────── */}
      {showAllSessions && (
        <AllSessionsOverlay
          sessions={sessions}
          getContext={getSessionContext}
          onResume={resumeSession}
          onDiscard={(id) => setConfirmDeleteId(id)}
          onClose={() => setShowAllSessions(false)}
        />
      )}

      {/* ── Delete Session Confirmation ─────────────────────────── */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6 animate-fade-in">
          <div className="bg-white rounded-2xl p-5 w-full max-w-xs shadow-xl text-center space-y-4 animate-slide-up">
            <div className="text-3xl">&#x1F5D1;</div>
            <p className="text-sm font-semibold text-squishy-navy">Discard this session?</p>
            <p className="text-xs text-slate-500">Your progress will be lost and can't be recovered.</p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition active:scale-[0.97]"
              >
                Keep
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition active:scale-[0.97]"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── New User Welcome Modal ───────────────────────────────── */}
      {showWelcome && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-6 animate-fade-in">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl text-center space-y-5">
            <div className="text-4xl">&#x1F44B;</div>
            <h2 className="text-xl font-bold text-squishy-navy">Welcome to Squishy!</h2>
            <p className="text-sm text-slate-500">What would you like to start with?</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/onboarding')}
                className="w-full bg-gradient-to-br from-squishy-primary to-teal-600 text-white py-4 rounded-xl font-bold text-base shadow-md active:scale-[0.97] transition-transform"
              >
                A Class
                <span className="block text-xs font-normal opacity-80 mt-0.5">Add your school &amp; class</span>
              </button>
              <button
                onClick={() => navigate('/exams')}
                className="w-full bg-gradient-to-br from-squishy-dark to-purple-600 text-white py-4 rounded-xl font-bold text-base shadow-md active:scale-[0.97] transition-transform"
              >
                A Professional Exam
                <span className="block text-xs font-normal opacity-80 mt-0.5">NCLEX, MCAT, USMLE &amp; more</span>
              </button>
            </div>
            <button
              onClick={() => setDismissedWelcome(true)}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              Skip for now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Smooth expand/collapse panel ────────────────────────────────────────────

function ExpandPanel({ open, children }: { open: boolean; children: React.ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (open && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [open]);

  return (
    <div
      className="overflow-hidden transition-all duration-300 ease-out"
      style={{ maxHeight: height, opacity: open ? 1 : 0 }}
    >
      <div ref={contentRef}>
        {children}
      </div>
    </div>
  );
}

// ── Session Card ────────────────────────────────────────────────────────────

function SessionCard({ session, contextName, onResume, onDiscard }: {
  session: StudySession;
  contextName: string;
  onResume: () => void;
  onDiscard: () => void;
}) {
  const progress = session.totalQuestions > 0
    ? Math.round((session.questionsAnswered / session.totalQuestions) * 100)
    : 0;
  const title = session.label || 'All Topics';

  return (
    <div
      onClick={onResume}
      className="w-40 shrink-0 rounded-xl p-3 flex flex-col gap-1 shadow-sm cursor-pointer active:scale-[0.97] transition-transform relative"
      style={{ background: 'linear-gradient(135deg, #2dd4bf, #14b8a6)' }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onDiscard(); }}
        className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-white/80">
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>
      <div className="text-xs font-bold text-white truncate pr-5">{title}</div>
      <div className="text-[10px] text-white truncate">{contextName}</div>
      <div className="h-1 bg-white/30 rounded-full overflow-hidden mt-1">
        <div className="h-full bg-white rounded-full" style={{ width: `${progress}%` }} />
      </div>
      <div className="text-[10px] text-white">{progress}% · {session.questionsAnswered}/{session.totalQuestions}</div>
    </div>
  );
}

// ── All Sessions Overlay ────────────────────────────────────────────────────

function AllSessionsOverlay({ sessions, getContext, onResume, onDiscard, onClose }: {
  sessions: StudySession[];
  getContext: (s: StudySession) => string;
  onResume: (s: StudySession) => void;
  onDiscard: (id: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div
        className="absolute inset-x-0 bottom-0 top-12 bg-white rounded-t-2xl p-5 overflow-y-auto animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-squishy-navy">All Sessions</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">&#x2715;</button>
        </div>
        <div className="space-y-3">
          {sessions.map((s) => {
            const progress = s.totalQuestions > 0
              ? Math.round((s.questionsAnswered / s.totalQuestions) * 100)
              : 0;
            const title = s.label || `${s.totalQuestions} questions`;
            return (
              <div key={s.id} className="border rounded-xl p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-squishy-navy truncate">{title}</div>
                  <div className="text-xs text-slate-500">{getContext(s)}</div>
                  <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-squishy-teal rounded-full" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{progress}% · {s.questionsAnswered}/{s.totalQuestions} · {s.xpEarned} XP</div>
                </div>
                <button
                  onClick={() => onResume(s)}
                  className="shrink-0 text-xs font-semibold py-2 px-4 rounded-lg bg-squishy-teal text-white hover:opacity-90 transition"
                >
                  Resume
                </button>
                <button
                  onClick={() => onDiscard(s.id)}
                  className="shrink-0 text-xs py-2 px-2 text-slate-400 hover:text-red-500 transition"
                >
                  &#x2715;
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
